import React, { Component } from "react";
import { inject, observer } from "mobx-react";
import { Col, Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import { confirmAlert } from 'react-confirm-alert';
import moment from 'moment';

// HOCs
import withLocalization from "../../../hoc/withLocalization";
import applicationRouter from "../../../hoc/applicationRouter";

// Components
import Button from '../../../components/CustomButton/CustomButton';
import LoadingSpinner from '~/elements/LoadingSpinner';
import Select from "../../../elements/Select";
import SwitchWidget from "../ControlPanel/SwitchWidget";
import EditWorkPatternModal from "./EditWorkPatternComp";
import WorkPatternHistoryModal from "./WorkPatternHistoryModal";
import { DATE_FORMAT } from "./RosterScheduler";
import Input from "../../../elements/Input";

// Utils
import { isWeekend } from '~/utils/commonMethods';
import { DEFAULT_VISIBILITY_WEEKS } from '~/utils/constants';

// Styles
import './RosterScheduler/css/workPatternStyle.css';


const DAY_JS_INDEX = { monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6, sunday: 0 };

const DAYS = [
    { key: 'monday', label: 'Mon' },
    { key: 'tuesday', label: 'Tue' },
    { key: 'wednesday', label: 'Wed' },
    { key: 'thursday', label: 'Thu' },
    { key: 'friday', label: 'Fri' },
    { key: 'saturday', label: 'Sat' },
    { key: 'sunday', label: 'Sun' },
];

const calculatePatternTotals = (patternData, totalWeeks, commonStore) => {
    let summaryShifts = 0;
    let summaryMinutes = 0;

    const weeks = Array.from({ length: totalWeeks }, (_, i) => {
        const weekData = patternData[i] || {};
        let shiftCount = 0;
        let weekMinutes = 0;

        Object.values(weekData).forEach(({ shiftCodeId, standbyCodeId }) => {
            if (standbyCodeId) {
                shiftCount++;
                const shiftInfo = commonStore.getCompanyShift(standbyCodeId);
                if (shiftInfo) weekMinutes += Math.round(shiftInfo.normal_work_day / shiftInfo.factor) || 0;
            }
            if (shiftCodeId) {
                shiftCount++;
                const shiftInfo = commonStore.getCompanyShift(shiftCodeId);
                if (shiftInfo) weekMinutes += shiftInfo.normal_work_day || 0;
            };
        });

        summaryShifts += shiftCount;
        summaryMinutes += weekMinutes;
        return { weekIndex: i, shiftCount, totalHours: (weekMinutes / 60).toFixed(2) };
    });

    return {
        weeks,
        totalShifts: summaryShifts,
        totalHours: (summaryMinutes / 60).toFixed(2),
        avgWeeklyHrs: totalWeeks > 0 ? (summaryMinutes / 60 / totalWeeks).toFixed(2) : '0.00',
    };
};

const getTabShifts = (companyShifts) => {
    const ws = [], fc= [], sb = [];
    for (const s of companyShifts) {
        if (s.status !== 'active')
            continue;
        if (s.shift_type === 'standbyShift') {
            sb.push(s);
        } else if (s.shift_type === 'freeShift') {
            fc.push(s);
        } else {
            ws.push(s);
        }
    }

    return { WS: ws, FC: fc, SB: sb };
}

class WorkPatternCreator extends Component {
    constructor(props) {
        super(props);
        this.state = {
            department: '',
            departmentOptions: [],
            selectedWeekIndex: 0,
            totalWeeks: 1,
            maxWeeks: 1,
            patternData: {},
            overtimeLimitPerWeek: 0,
            isInitialLoading: false,
            isSaving: false,
            templateName: '',
            isAutomation: false,
            isEditMode: false,
            editTemplateId: null,
            originalPatternData: null,
            originalTotalWeeks: null,
            applyFromModal: null,
            employees: [],
            showHistory: false,
            visibilityWeeks: DEFAULT_VISIBILITY_WEEKS,
            activeTab: 'WS'
        }
    }

    componentDidMount() {
        const editTemplateId = this.props.router?.location?.state?.editTemplateId;
        if (editTemplateId) {
            this.setState({ isEditMode: true, editTemplateId }, () => this.loadDepartment());
        } else {
            this.loadDepartment();
        }
    }

    loadDepartment = async () => {
        const { rosterStore, router, userStore } = this.props;
        const { isEditMode, editTemplateId } = this.state;
        const getDepartments = await rosterStore.getRuleBasedDepartments({ rule: 'work_hours_rules' });
        const availableDepartments = getDepartments?.availableDepartment || [];

        let firstDept = '';
        let editPattern = null;

        if (isEditMode && editTemplateId) {
            try {
                let pmMembers = '';
                if (userStore.currentUser && userStore.currentUser.user_type !== 'admin' && userStore.currentUser.Manages && userStore.currentUser.Manages.length) {
                    pmMembers = userStore.currentUser.Manages.join(',');
                }
                const res = await rosterStore.getWorkPatternSelectionDetails({ template_id: editTemplateId, pmMembers });
                editPattern = res || null;
                if (editPattern?.department) firstDept = editPattern.department || '';
            } catch (error) {
                console.error('Error fetching work pattern:', error);
            }
        }

        if (!firstDept) {
            const routerDept = router?.location?.state?.department;
            const matched = availableDepartments.find(d => d.value === routerDept?.toLowerCase());
            firstDept = matched?.value || availableDepartments[0]?.value || '';
        }

        this.setState({ departmentOptions: availableDepartments, department: firstDept }, () => {
            this.loadRules(editPattern);
        });
    }

    loadRules = async (editPattern) => {
        const { clientStore } = this.props;
        const { department } = this.state;
        this.setState({ isInitialLoading: true });

        const workHoursRule = await clientStore.getWorkHoursRuleByDepartment(department);
        const maxWeeks = workHoursRule?.cycle_week_length || 1;
        const visibilityWeeks = workHoursRule?.work_pattern_visibility_weeks || DEFAULT_VISIBILITY_WEEKS;
        this.setState({ visibilityWeeks });

        if (editPattern) {
            const patternData = {};
            (editPattern.weeks || []).forEach(w => {
                patternData[w.week_number - 1] = w.shift_data || {};
            });
            this.setState({
                maxWeeks,
                overtimeLimitPerWeek: workHoursRule?.mandatory_overtime_limit_per_week || 0,
                templateName: editPattern.label || '',
                isAutomation: !!editPattern.automation,
                patternData,
                totalWeeks: editPattern.weeks?.length || 1,
                originalPatternData: patternData,
                originalTotalWeeks: editPattern.weeks?.length || 1,
                isInitialLoading: false,
                employees: editPattern.employees || [],
            });
        } else {
            this.setState({
                totalWeeks: 1,
                maxWeeks,
                overtimeLimitPerWeek: workHoursRule?.mandatory_overtime_limit_per_week || 0,
                patternData: {},
                isInitialLoading: false,
            });
        }
    }

    handleDepartmentChange = (value) => {
        this.setState({ department: value, selectedWeekIndex: 0 }, () => this.loadRules());
    }

    handleShiftSelect = (dayKey, shift) => {
        const { selectedWeekIndex, patternData } = this.state;
        const { commonStore, t } = this.props;
        const weekData = patternData[selectedWeekIndex] || {};
        const isDeselecting = weekData[dayKey]?.shiftCodeId === shift.id || weekData[dayKey]?.standbyCodeId === shift.id || weekData[dayKey]?.fCodeId === shift.id;
        const newWeekData = { ...weekData };
        const field = shift.shift_type === 'standbyShift' ? 'standbyCodeId' : shift.shift_type === 'freeShift' ? 'fCodeId' :'shiftCodeId';
        const isF1AlreadySelectedForTheWeek = Object.values(weekData).some(day => commonStore.companyShiftMap.get(day.fCodeId)?.short_code === 'F1');
        if (!isDeselecting && isF1AlreadySelectedForTheWeek && shift.shift_type === 'freeShift' && shift.short_code === 'F1')
            return commonStore.addNotification(t('F1 can only be assigned once per week'), null, 'warning');

        if (isDeselecting)
            delete newWeekData[dayKey][field];
        else {
            newWeekData[dayKey] = {...newWeekData[dayKey],  [field]: shift.id };
            if (shift.shift_type === 'freeShift') {
                delete newWeekData[dayKey]['shiftCodeId'];
                delete newWeekData[dayKey]['standbyCodeId'];
            }
            else 
                delete newWeekData[dayKey]['fCodeId'];
        }

        if (Object.keys(newWeekData[dayKey]).length === 0)
            delete newWeekData[dayKey];

        this.setState({ patternData: { ...patternData, [selectedWeekIndex]: newWeekData } });
    }

    handleDeleteWeek = (indexToDelete) => {
        const { patternData, totalWeeks, selectedWeekIndex } = this.state;
        const newPatternData = {};

        let newIdx = 0;
        for (let i = 0; i < totalWeeks; i++) {
            if (i === indexToDelete) continue;
            newPatternData[newIdx] = patternData[i] || {};
            newIdx++;
        }

        let newSelectedIndex = selectedWeekIndex;
        if (selectedWeekIndex > indexToDelete) {
            newSelectedIndex = selectedWeekIndex - 1;
        } else if (selectedWeekIndex >= totalWeeks - 1) {
            newSelectedIndex = Math.max(0, totalWeeks - 2);
        }

        this.setState({
            patternData: newPatternData,
            totalWeeks: totalWeeks - 1,
            selectedWeekIndex: newSelectedIndex
        });
    }

    hasWeeksChanged = (weeks) => {
        const { originalPatternData, originalTotalWeeks, totalWeeks } = this.state;
        if (!originalPatternData) return false;
        if (totalWeeks !== originalTotalWeeks) return true;
        for (let i = 0; i < weeks.length; i++) {
            const original = originalPatternData[i] || {};
            if (JSON.stringify(weeks[i].shift_data) !== JSON.stringify(original)) return true;
        }
        return false;
    }

    doSave = async (payload) => {
        const { rosterStore, commonStore, t, router } = this.props;
        const { isEditMode, editTemplateId } = this.state;
        this.setState({ isSaving: true });
        try {
            const result = isEditMode
                ? await rosterStore.updateWorkPattern({ currentWorkPatternId: editTemplateId, ...payload })
                : await rosterStore.createWorkPatternTemplate(payload);

            if (result?.conflict) {
                confirmAlert({
                    title: t('Newer Version Exists'),
                    message: result.message,
                    buttons: [
                        {
                            label: t('Yes, proceed'),
                            onClick: () => this.doSave({ ...payload, confirm_version_override: true }),
                        },
                        { label: t('Cancel') },
                    ],
                });
                return;
            }

            if (result) {
                commonStore.addNotification(result?.message || t('Work pattern saved successfully'), null, 'success');
                router.navigate('/admin/roster');
            }
        } catch (err) {
            commonStore.addNotification(err?.message || t('Something went wrong'), null, 'error');
        } finally {
            this.setState({ isSaving: false });
        }
    }

    updateApplyModal = (updates) => {
        this.setState(prev => ({ applyFromModal: { ...prev.applyFromModal, conflicts: [], ...updates } }));
    };

    getBasePayload = () => {
        const { templateName, isAutomation, patternData, totalWeeks, department } = this.state;

        const weeks = Array.from({ length: totalWeeks }, (_, i) => ({
            week_number: i + 1,
            shift_data: patternData[i] || {},
        }));

        return { name: templateName.trim(), automation: isAutomation, department, weeks, is_work_pattern: true };
    };

    buildApplyPayload = () => {
        const { applyFromModal, employees } = this.state;
        const { userStore } = this.props;
        const allUserIds = employees.map(e => e.user_id);
        const isSubset = applyFromModal.isSubset && applyFromModal.selectedUserIds?.length > 0 && applyFromModal.selectedUserIds.length < allUserIds.length;

        let pm_members = [];
        if (userStore.currentUser && userStore.currentUser.user_type !== 'admin' && userStore.currentUser.Manages && userStore.currentUser.Manages.length) {
            pm_members = userStore.currentUser.Manages;
        }

        return {
            ...this.getBasePayload(),
            apply_from: applyFromModal.selectedDate,
            is_subset: isSubset,
            new_template_name: isSubset ? applyFromModal.newTemplateName : null,
            new_template_automation: isSubset ? applyFromModal.isAutomation : null,
            override_reason: moment(applyFromModal.selectedDate).isBefore(moment().add((this.state.visibilityWeeks || DEFAULT_VISIBILITY_WEEKS) * 7, 'days'), 'day')
                ? applyFromModal.overrideReason?.trim() || null
                : null,
            pm_members,
            ...(isSubset && { apply_to_user_ids: applyFromModal.selectedUserIds }),
            ...(isSubset && { cycle_week_offsets: applyFromModal.cycleWeekOffsets || {} }),
        };
    };

    handleCheckConflicts = async (data) => {
        const { rosterStore } = this.props;
        const userIds = data.apply_to_user_ids?.length > 0 ? data.apply_to_user_ids : this.state.employees.map(e => e.user_id);

        const result = await rosterStore.validateAssignmentConflicts({
            userIds,
            assignmentStartDate: moment(data.apply_from).format(DATE_FORMAT),
            workPatternId: this.state.editTemplateId,
        });
        return result?.conflict || [];
    };

    handleApplyFromConfirm = async () => {
        const { applyFromModal, employees } = this.state;
        const { commonStore, t } = this.props;

        if (!applyFromModal?.selectedDate) return;

        if (applyFromModal.isSubset && !applyFromModal.selectedUserIds?.length) {
            return commonStore.addNotification(t('Please select users'), null, 'warning');
        }

        const allUserIds = employees.map(e => e.user_id);
        const isTrueSubset = applyFromModal.isSubset
            && applyFromModal.selectedUserIds?.length > 0
            && applyFromModal.selectedUserIds.length < allUserIds.length;

        if (isTrueSubset && !applyFromModal.newTemplateName?.trim()) {
            return commonStore.addNotification(t('Please provide a name for the new template'), null, 'warning');
        }

        const isEarlyDate = moment(applyFromModal.selectedDate).isBefore(moment().add((this.state.visibilityWeeks || DEFAULT_VISIBILITY_WEEKS) * 7, 'days'), 'day');
        if (isEarlyDate && !applyFromModal.overrideReason?.trim()) {
            return commonStore.addNotification(t('Please provide a reason for the short notice change.'), null, 'warning');
        }

        const payload = this.buildApplyPayload();

        try {
            const conflicts = await this.handleCheckConflicts(payload);
            if (conflicts?.length > 0) {
                this.setState(prev => ({ applyFromModal: { ...prev.applyFromModal, conflicts } }));
                return;
            }
        } catch (err) {
            commonStore.addNotification(err?.message || t('Something went wrong. Please try again.'), null, 'error');
            return;
        }

        await this.doSave(payload);
        this.setState({ applyFromModal: null });
    };

    handleConfirmAnyway = async () => {
        const payload = { ...this.buildApplyPayload(), overlapConflicts: true };
        await this.doSave(payload);
        this.setState({ applyFromModal: null });
    };

    handleCreateCopy = () => {
        const { patternData, totalWeeks } = this.state;
        this.setState({
            isEditMode: false,
            editTemplateId: null,
            templateName: '',
            employees: [],
            originalPatternData: null,
            originalTotalWeeks: null,
            patternData: Object.assign({}, patternData),
            totalWeeks,
            selectedWeekIndex: 0,
        });
    };

    handleDeactivate = () => {
        const { rosterStore, commonStore, t, router } = this.props;
        const { editTemplateId, templateName } = this.state;
        confirmAlert({
            title: t('Deactivate Work Pattern'),
            message: `${t('Are you sure you want to deactivate')} "${templateName}"? ${t('Once deactivated, this work pattern cannot be assigned in the future. Existing assignments will remain unchanged.')}`,
            buttons: [
                {
                    label: t('Deactivate'),
                    onClick: async () => {
                        try {
                            const res = await rosterStore.deactivateWorkPattern(editTemplateId);
                            commonStore.addNotification(res?.message || t('Work pattern deactivated successfully'), null, 'success');
                            router.navigate('/admin/roster');
                        } catch (err) {
                            commonStore.addNotification(err?.message || t('Something went wrong'), null, 'error');
                        }
                    }
                },
                { label: t('Cancel'), onClick: () => { } }
            ]
        });
    };

    handleConfirmSave = async () => {
        const { commonStore, t } = this.props;
        const { templateName, isEditMode, employees } = this.state;

        if (!templateName.trim()) return commonStore.addNotification(t('Template Name is Required'), null, 'error');

        const payload = this.getBasePayload();
        const { weeks } = payload;

        const emptyWeeks = weeks.filter(w => Object.keys(w.shift_data).length === 0).map(w => w.week_number);

        if (emptyWeeks.length === weeks.length) {
            return commonStore.addNotification(t('Please assign at least one shift to this pattern'), null, 'error');
        }

        const proceedWithSave = async () => {
            if (isEditMode && this.hasWeeksChanged(weeks) && employees?.length > 0) {
                const isPm = this.props.userStore.currentUser.user_type === 'pm';
                this.setState({
                    applyFromModal: {
                        isSubset: isPm ? true : null,
                        selectedDate: moment().add((this.state.visibilityWeeks || DEFAULT_VISIBILITY_WEEKS) * 7, 'days').format(DATE_FORMAT),
                        selectedUserIds: [],
                        newTemplateName: '',
                        overrideReason: '',
                        isAutomation: false,
                        cycleWeekOffsets: {},
                    }
                });
                return;
            }
            await this.doSave(payload);
        };

        if (emptyWeeks.length > 0) {
            confirmAlert({
                title: t('Are You Sure'),
                message: `${t('Weeks')} ${emptyWeeks.join(', ')} ${t('are empty and will be saved as Free. Do you want to proceed?')}`,
                buttons: [
                    { label: t('Yes'), onClick: proceedWithSave },
                    { label: t('No') }
                ],
            });
        } else {
            proceedWithSave();
        }
    };

    render() {
        const { t, commonStore, router } = this.props;
        const {
            department, departmentOptions, selectedWeekIndex, totalWeeks, maxWeeks, activeTab,
            patternData, overtimeLimitPerWeek, isInitialLoading, isSaving, templateName, isAutomation, isEditMode, editTemplateId,
            applyFromModal, showHistory, employees
        } = this.state;

        if (isInitialLoading) return <LoadingSpinner />;

        const companyShifts = commonStore.sortCompanyShifts(commonStore?.config?.companyShifts, false, { F1: true, F2: true, F3: false, F4: false, F5: false }, true);
        const currentWeekData = patternData[selectedWeekIndex] || {};
        const clientDefaultHolidays = commonStore.config?.client?.data?.Vacation_setup?.default_holidays_for_vacation || '';

        const stats = calculatePatternTotals(patternData, totalWeeks, commonStore);
        const selectedDept = departmentOptions.find(d => d.value === department)?.label || department;
        const isDeleteWeekDisabled = isEditMode && employees.length > 0;

        const allowStandbyCalls = !!(commonStore.config?.client?.data?.basicRules?.urgentWorkSettings?.allowStandbyCalls);
        const allowFreeCodes = companyShifts.filter(ele => ele?.status === 'active' && ['F1', 'F2'].includes(ele?.short_code))
        const visibleTabs = ['WS', allowFreeCodes.length && 'FC', allowStandbyCalls && 'SB'].filter(Boolean);
        const resolvedTab = visibleTabs.includes(activeTab) ? activeTab : 'WS';
        const activeShifts = getTabShifts(companyShifts)[resolvedTab];

        return (
            <div className="work-pattern-creator-wrapper">
                <Row className='mb-3 align-items-end mx-0'>
                    <Col md={3}>
                        <Input
                            label={<><span className="fw-600 fs15">{t('Template')} {t('Name')}</span>
                                <span className="text-danger ms-1">*</span></>}
                            value={templateName}
                            placeholder={t('e.g. High Season')}
                            onChange={e => this.setState({ templateName: e.target.value })}
                        />
                    </Col>
                    <Col md={2}>
                        <label className="fw-600 mb-1 fs15">{t('Department')}</label>
                        <Select
                            options={departmentOptions}
                            value={departmentOptions.find(d => d.value === department)}
                            onChange={e => this.handleDepartmentChange(e?.value)}
                        />
                    </Col>
                    <Col md={2} className="pb-1">
                        <div className='d-flex align-items-center gap-2'>
                            <SwitchWidget
                                value={isAutomation}
                                onChange={checked => this.setState({ isAutomation: checked })}
                                className="mt-2"
                            />
                            <span className="fw-600 fs15">{t('Automation')}</span>
                            <span className={`status-box status-box_${isAutomation ? 'active' : 'inactive'}`}>
                                {t(isAutomation ? 'On' : 'Off ')}
                            </span>
                        </div>
                    </Col>
                    <Col md={5} className='d-flex justify-content-end gap-2'>
                        <Button onClick={() => router.navigate('/admin/roster')}>{t('Cancel')}</Button>
                        {isEditMode && (
                            <Button onClick={() => this.setState({ showHistory: true })}>
                                <i className="fa fa-history me-1" />{t('History')}
                            </Button>
                        )}
                        {isEditMode && (
                            <OverlayTrigger placement="bottom" overlay={<Tooltip id="copy-pattern-tooltip">{t('Copy')} {t('Work Pattern')} {t('Template')}</Tooltip>}>
                                <Button onClick={this.handleCreateCopy}>
                                    <i className="fa fa-copy" />
                                </Button>
                            </OverlayTrigger>
                        )}
                        {isEditMode && (
                            <OverlayTrigger placement="bottom" overlay={<Tooltip id="deactivate-pattern-tooltip">{t('Deactivate')} {t('Work Pattern')}</Tooltip>}>
                                <Button danger onClick={this.handleDeactivate}>
                                    <i className="fa fa-ban" />
                                </Button>
                            </OverlayTrigger>
                        )}
                        <Button fill dynamic_lg disabled={isSaving} onClick={this.handleConfirmSave}>
                            {isSaving ? t('Saving...') : isEditMode ? `${t('Update')} ${t('Template')}` : `${t('Save')} ${t('Template')}`}
                        </Button>
                    </Col>
                </Row>

                <Row className="mx-0 mb-3">
                    <Col xs={12}>
                        <div style={{ borderLeft: '4px solid #2550AC', borderRadius: '4px' }} className="py-2 px-3 text-primary-color">
                            <div className="fw-bold small"><i className="fas fa-info-circle me-2"></i>{t('How This Work Pattern Works')}</div>
                            <div className="small">
                                <span>{t('This work pattern will be used to automatically or manually generate schedules.')} {t('Department')} <b>{selectedDept}</b> {t('allows up to')} <b>{maxWeeks}</b> {t('Weeks')} {t('in a cycle')}.</span>
                                {totalWeeks < maxWeeks
                                    ?
                                    <span>{t('You have added')} <b>{totalWeeks}</b> {t('week(s)')}.  {t('The pattern will repeat from')} <b>{t('Week')} 1</b> {t('when schedules are generated')}.</span>
                                    :
                                    <span>{t('All')} <b>{maxWeeks}</b> {t('weeks are set')} {t('The pattern will repeat from')} <b>{t('Week')} 1</b> {t('after the last week')}.</span>
                                }
                            </div>
                        </div>
                    </Col>
                </Row>

                <Row className="mx-0">
                    <Col xs={10} className="px-0">
                        <div className="modal-container" style={{ overflowY: 'auto', overflowX: 'hidden', height: '65vh' }}>
                            <Row className="mx-0">
                                {DAYS.map(({ key, label }) => {
                                    const isDayWeekend = isWeekend(DAY_JS_INDEX[key], clientDefaultHolidays);
                                    return (
                                        <Col key={key} lg={4} md={6} className="mb-4">
                                            <div className={`weekly-shift-day-card ${isDayWeekend ? 'weekly-shift-day-body-weekend' : 'weekly-shift-day-body-weekday'}`}>
                                                <div className={`d-flex align-items-center justify-content-between p-2 weekly-shift-day-header ${isDayWeekend ? 'weekly-shift-day-header-weekend' : 'weekly-shift-day-header-weekday'}`}>
                                                    <span className="fw-bold fs15">{t(label)}</span>
                                                    {!currentWeekData[key] && (
                                                        <span className="badge bg-light text-muted fst-italic fw-semibold">
                                                            {t('Free')}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="d-flex w-100 flex-column gap-2">
                                                    <div className="d-flex" style={{ borderBottom: '1px solid #dee2e6', paddingLeft: '0.9rem', paddingRight: '0.9rem' }}>
                                                        {visibleTabs.map(tabKey => (
                                                            <button
                                                                key={tabKey}
                                                                onClick={() => this.setState({ activeTab: tabKey })}
                                                                className="d-flex align-items-center gap-1 border-0 bg-transparent px-3 py-2 fs-12 fw-bold cursor-pointer"
                                                                style={{
                                                                    borderBottom: resolvedTab === tabKey ? '2px solid #0d6efd' : '2px solid transparent',
                                                                    color: resolvedTab === tabKey ? '#0d6efd' : '#6c757d',
                                                                    marginBottom: '-1px',
                                                                }}
                                                            >
                                                                {tabKey}
                                                                <span
                                                                    className="badge rounded-pill ms-1"
                                                                    style={{
                                                                        backgroundColor: Object.keys(currentWeekData[key] || {}).length > 0 ? '#0d6efd' : '#dee2e6',
                                                                        color: Object.keys(currentWeekData[key] || {}).length > 0 ? '#fff' : '#6c757d',
                                                                        fontSize: '10px',
                                                                    }}
                                                                >
                                                                    {tabKey === 'SB' && currentWeekData[key]?.standbyCodeId ? 1 : ''}
                                                                    {tabKey === 'WS' && currentWeekData[key]?.shiftCodeId ? 1 : ''}
                                                                    {tabKey === 'FC' && currentWeekData[key]?.fCodeId ? 1 : ''}
                                                                </span>
                                                            </button>
                                                        ))}
                                                    </div>
                                                    <div className="p-3 d-flex flex-wrap gap-2">
                                                        {activeShifts.map((shift, idx) => {
                                                            const isSelected = currentWeekData[key]?.shiftCodeId === shift.id || currentWeekData[key]?.standbyCodeId === shift.id || currentWeekData[key]?.fCodeId === shift.id;
                                                            return (
                                                                <div
                                                                    key={`shift-${idx}`}
                                                                    onClick={() => this.handleShiftSelect(key, shift) }
                                                                    style={{ backgroundColor: !isSelected && shift.shift_color ? shift.shift_color : '' }}
                                                                    className={`fs-12 d-flex justify-content-center align-items-center weekly-shift-code-button ${isSelected ? ' weekly-shift-code-button-selected' : ''}`}
                                                                >
                                                                    {shift.short_code}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                    );
                                })}
                            </Row>
                        </div>
                    </Col>

                    <Col xs={2}>
                        <div className="weekly-shift-cycle-data-container mb-3">
                            <div className="d-flex justify-content-start align-items-center p-2 weekly-shift-cycle-data-header">
                                <span className="fw-bold fs14">{t('Cycle')} {t('Details')}</span>
                            </div>
                            <div className="weekly-shift-cycle-data-body">
                                <div className="d-flex flex-column gap-1">
                                    <div className="d-flex justify-content-between">
                                        <span className="text-muted">{t('Assigned')} {t('shifts')}: </span>
                                        <span className="fw-bold">{stats.totalShifts || '-'}</span>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                        <span className="text-muted">{t('Assigned')} {t('Hours')}:</span>
                                        <span className="fw-bold">{stats.totalHours || '-'}</span>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                        <span className="text-muted">{t('Avg. weekly hrs')}:</span>
                                        <span className={`fw-bold ${parseFloat(stats.avgWeeklyHrs) * 60 > overtimeLimitPerWeek ? 'average-hours-high' : 'average-hours-normal'}`}>
                                            {stats.avgWeeklyHrs || '-'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="weekly-shift-weeks-container">
                            <div className="d-flex justify-content-start align-items-center p-2 weekly-shift-weeks-header">
                                <span className="fw-bold fs15">
                                    {t('Cycle')} {t('Weeks')} ({totalWeeks} {t('Weeks')})
                                </span>
                            </div>
                            <div className="weekly-shift-weeks-body" style={{ maxHeight: '40vh', overflowY: 'auto' }}>
                                <div className="d-flex flex-column gap-2 p-1">
                                    {stats.weeks.map(w => {
                                        const currentWeekMinutes = parseFloat(w.totalHours) * 60;
                                        const isOvertime = overtimeLimitPerWeek > 0 && currentWeekMinutes > overtimeLimitPerWeek;
                                        const isActive = w.weekIndex === selectedWeekIndex;

                                        return (
                                            <div key={w.weekIndex} className="position-relative">
                                                <button
                                                    className={`weekly-shift-week-navigation-btn w-100 ${isOvertime ? 'text-danger' : 'text-primary-color'}`}
                                                    onClick={() => this.setState({ selectedWeekIndex: w.weekIndex })}
                                                >
                                                    <span className={`fw-600 fs12
                                                        ${isOvertime ? 'text-danger' : isActive ? 'text-light' : 'text-primary-color'}
                                                        ${isActive ? 'active-bg' : ''}`}
                                                    >
                                                        <span className="fw-600 fs14"> {t('Week')} {w.weekIndex + 1} </span>
                                                        {w.shiftCount === 0 ? ' (-/-)' : ` (${w.totalHours}${t('hr')}/${w.shiftCount} ${t('shifts')})`}
                                                    </span>
                                                </button>
                                                {totalWeeks > 1 && (
                                                    <i
                                                        className={`far fa-trash-alt position-absolute top-0 end-0 bg-white p-1 ${isDeleteWeekDisabled ? 'text-muted cursor-no-drop opacity-50' : 'text-danger cursor-pointer'}`}
                                                        onClick={(e) => {
                                                            if (isDeleteWeekDisabled) return;
                                                            e.stopPropagation();
                                                            this.handleDeleteWeek(w.weekIndex);
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        );
                                    })}

                                    {totalWeeks < maxWeeks && (
                                        <button
                                            className={`add-week-btn mt-1${isDeleteWeekDisabled ? ' opacity-50 cursor-no-drop' : ''}`}
                                            disabled={isDeleteWeekDisabled}
                                            onClick={() => {
                                                if (isDeleteWeekDisabled) return;
                                                this.setState(prev => ({ totalWeeks: prev.totalWeeks + 1 }));
                                            }}
                                        >
                                            + {t('Add')} {t('Week')}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </Col>
                </Row>

                <EditWorkPatternModal
                    show={!!applyFromModal}
                    applyWorkPatternData={{ ...applyFromModal, employees: this.state.employees }}
                    handleUpdate={this.updateApplyModal}
                    handleClose={() => this.setState({ applyFromModal: null })}
                    handleSave={this.handleApplyFromConfirm}
                    onConfirmAnyway={this.handleConfirmAnyway}
                    conflicts={applyFromModal?.conflicts || []}
                    isSaving={isSaving}
                    cycleWeekLength={this.state.totalWeeks}
                    visibilityWeeks={this.state.visibilityWeeks}
                    isPm={this.props.userStore.currentUser.user_type === 'pm'}
                    t={t}
                />

                <WorkPatternHistoryModal
                    show={showHistory}
                    templateId={editTemplateId}
                    handleClose={() => this.setState({ showHistory: false })}
                />
            </div>
        );
    }
}

export default inject('commonStore', 'clientStore', 'rosterStore', 'userStore')(withLocalization(applicationRouter(observer(WorkPatternCreator))));
